import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CreditCard, CheckCircle, AlertTriangle, Clock, ExternalLink, Loader2 } from 'lucide-react';
import { useOrganization } from '../contexts/OrganizationContext';
import { getSubscriptionStatus, createCheckoutSession, createPortalSession } from '../services/ApiService';
import type { SubscriptionStatus } from '../services/ApiService';
import { AnalyticsEvents, track } from '../services/AnalyticsService';
import { PRICING_TIERS } from '../constants/pricing';

// Stripe confirms the payment through a webhook, which may land shortly after the user is sent back here.
const CONFIRM_POLL_INTERVAL_MS = 2000;
const CONFIRM_MAX_ATTEMPTS = 15;

export default function BillingPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const { currentOrg, isBoardMember, refreshOrganizations } = useOrganization();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnedFromCheckout = searchParams.get('checkout') === 'success';
  const [confirming, setConfirming] = useState(returnedFromCheckout);
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;

    const load = async () => {
      try {
        const result = await getSubscriptionStatus(orgId);
        if (cancelled) return;
        setStatus(result);
        setLoading(false);
        if (!returnedFromCheckout) return;

        attempts++;
        if (result.status !== 'active' && attempts < CONFIRM_MAX_ATTEMPTS) {
          timer = setTimeout(load, CONFIRM_POLL_INTERVAL_MS);
          return;
        }
        if (result.status !== 'active') {
          setError('Betalningen har inte bekräftats ännu. Ladda om sidan om en stund.');
        }
        setConfirming(false);
        setSearchParams({}, { replace: true });
        await refreshOrganizations();
      } catch {
        if (cancelled) return;
        setError('Kunde inte hämta prenumerationsstatus.');
        setConfirming(false);
        setLoading(false);
      }
    };

    setLoading(true);
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  // Runs once per org: setSearchParams changes identity when it clears ?checkout, which would restart the confirmation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const billingUrl = `${window.location.origin}${window.location.pathname}`;

  const handleStartSubscription = async () => {
    if (!orgId) return;
    setActionLoading(true);
    try {
      const { checkoutUrl } = await createCheckoutSession(orgId, `${billingUrl}?checkout=success`, billingUrl);
      track(AnalyticsEvents.CheckoutStarted, { orgId });
      window.location.href = checkoutUrl;
    } catch {
      setError('Kunde inte starta betalningsflödet. Försök igen.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdatePayment = async () => {
    if (!orgId) return;
    setActionLoading(true);
    try {
      const { portalUrl } = await createPortalSession(orgId, billingUrl);
      window.location.href = portalUrl;
    } catch {
      setError('Kunde inte öppna betalningsportalen. Försök igen.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  const effectiveStatus = status?.status ?? currentOrg?.subscriptionStatus ?? 'trial';
  const tier = status ? PRICING_TIERS[status.tier] : undefined;
  const canStartSubscription = isBoardMember && !!status?.checkoutEnabled;

  const getTrialDaysLeft = (): number => {
    const endsAt = status?.trialEndsAt ?? currentOrg?.trialEndsAt;
    if (!endsAt) return 0;
    const diff = new Date(endsAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const getPaidUntilFormatted = (): string => {
    const paidUntil = status?.paidUntil ?? currentOrg?.subscriptionPaidUntil;
    if (!paidUntil) return '';
    return new Date(paidUntil).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Fakturering</h1>
        <p className="text-gray-500 mt-1">{currentOrg?.name}</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {confirming && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 flex items-center gap-4">
          <Loader2 className="w-6 h-6 text-blue-600 animate-spin flex-shrink-0" />
          <h2 className="font-semibold text-blue-900">Bekräftar betalning…</h2>
        </div>
      )}

      {tier && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">{tier.name}</p>
              <p className="text-sm text-gray-500">{tier.members}</p>
            </div>
            <p className="font-semibold text-gray-900">{tier.price}</p>
          </div>
          {effectiveStatus !== 'active' && (
            <p className="text-xs text-gray-500 mt-3">Er nivå baseras på antalet fastigheter i föreningen.</p>
          )}
        </div>
      )}

      {!confirming && effectiveStatus === 'active' && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-semibold text-green-900">Prenumeration aktiv</h2>
              <p className="text-green-700 text-sm mt-1">
                {status?.cancelAtPeriodEnd
                  ? `Er prenumeration avslutas ${getPaidUntilFormatted()}.`
                  : `Er prenumeration förnyas ${getPaidUntilFormatted()}.`}
              </p>
              {isBoardMember && (
                <button
                  onClick={handleUpdatePayment}
                  disabled={actionLoading}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white border border-green-300 text-green-800 rounded-lg text-sm font-medium hover:bg-green-50 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                  Hantera betalningsuppgifter
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!confirming && effectiveStatus === 'trial' && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <Clock className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-semibold text-blue-900">Provperiod aktiv</h2>
              <p className="text-blue-700 text-sm mt-1">
                {getTrialDaysLeft()} dagar kvar av er gratis provperiod.
              </p>
              <p className="text-blue-600 text-sm mt-2">
                Efter provperioden krävs en aktiv prenumeration för att fortsätta använda tjänsten.
              </p>
              {canStartSubscription && (
                <button
                  onClick={handleStartSubscription}
                  disabled={actionLoading}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  Aktivera prenumeration
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!confirming && effectiveStatus === 'payment_failed' && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-semibold text-orange-900">Betalning misslyckades</h2>
              <p className="text-orange-700 text-sm mt-1">
                Er betalning verkar inte gått igenom, vänligen se över era kreditkorts uppgifter.
              </p>
              <p className="text-orange-600 text-sm mt-2">
                Uppdatera era kortuppgifter för att återställa tillgång till alla funktioner.
              </p>
              {isBoardMember && (
                <button
                  onClick={handleUpdatePayment}
                  disabled={actionLoading}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  Uppdatera betalningsuppgifter
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!confirming && effectiveStatus === 'expired' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-semibold text-red-900">Prenumeration har löpt ut</h2>
              <p className="text-red-700 text-sm mt-1">
                Er prenumeration har löpt ut. Aktivera en prenumeration för att återfå full tillgång.
              </p>
              {canStartSubscription && (
                <button
                  onClick={handleStartSubscription}
                  disabled={actionLoading}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  Aktivera prenumeration
                </button>
              )}
              {!isBoardMember && (
                <p className="text-red-600 text-sm mt-3">
                  Kontakta er styrelsemedlem för att aktivera prenumerationen.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
